import { useState } from "react";
import { Captions } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import api, { apiError } from "@/lib/api";
import CaptionPreview from "./CaptionPreview";

const STYLES = [
  { key: "classic", label: "Classic white", preview: <span className="font-black text-white [text-shadow:1px_1px_0_#000,-1px_1px_0_#000,1px_-1px_0_#000,-1px_-1px_0_#000]">BOLD TEXT</span> },
  { key: "yellow", label: "Yellow pop", preview: <span className="font-black text-yellow-300 [text-shadow:1px_1px_0_#000,-1px_1px_0_#000,1px_-1px_0_#000,-1px_-1px_0_#000]">BOLD TEXT</span> },
  { key: "karaoke", label: "Karaoke", preview: <span className="font-black [text-shadow:1px_1px_0_#000,-1px_1px_0_#000,1px_-1px_0_#000,-1px_-1px_0_#000]"><span className="text-yellow-300">BOLD</span> <span className="text-white">TEXT</span></span> },
];

const POSITIONS = [
  { key: "top", label: "Top" },
  { key: "middle", label: "Middle" },
  { key: "bottom", label: "Bottom" },
];

const SIZES = [
  { key: "small", label: "Small" },
  { key: "standard", label: "Standard" },
  { key: "large", label: "Large" },
];

export default function CaptionsCard({ videoId, enabled, style, position, size, sampleText, onSaved }) {
  const [busy, setBusy] = useState(false);

  const current = style || "classic";
  const currentPos = position || "bottom";
  const currentSize = size || "standard";

  const save = async (nextEnabled, nextStyle, nextPosition, nextSize) => {
    setBusy(true);
    try {
      await api.put(`/videos/${videoId}/captions`, { enabled: nextEnabled, style: nextStyle, position: nextPosition, size: nextSize });
      toast.success(nextEnabled ? "Captions updated — build the video to burn them in" : "Captions off for the next build");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-white/10 rounded-md bg-[#121212] p-4" data-testid="captions-card">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-8 w-8 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
            <Captions className="h-4 w-4 text-amber-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold">Bold on-screen captions</p>
            <p className="text-xs text-zinc-500">
              Burn the narration into the video as big bold text — most Shorts &amp; TikTok viewers watch muted.
            </p>
          </div>
        </div>
        <Switch
          checked={!!enabled}
          onCheckedChange={(c) => save(c, current, currentPos, currentSize)}
          disabled={busy}
          data-testid="captions-toggle"
          className="data-[state=checked]:bg-amber-500"
        />
      </div>
      {enabled && (
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="caption-style-picker">
          {STYLES.map((s) => (
            <button
              key={s.key}
              onClick={() => save(true, s.key, currentPos, currentSize)}
              disabled={busy}
              data-testid={`caption-style-${s.key}`}
              className={`rounded-md border px-2 py-2.5 text-center transition-colors ${
                current === s.key
                  ? "border-amber-500/60 bg-amber-500/10"
                  : "border-white/10 bg-[#0A0A0A] hover:border-white/25"
              }`}
            >
              <span className="block text-[13px] bg-zinc-800/80 rounded px-1 py-1 mb-1.5">{s.preview}</span>
              <span className={`text-[11px] ${current === s.key ? "text-amber-300 font-semibold" : "text-zinc-500"}`}>{s.label}</span>
            </button>
          ))}
        </div>
      )}
      {enabled && (
        <div className="mt-2 flex items-center gap-2" data-testid="caption-position-picker">
          <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold shrink-0">Position</span>
          <div className="grid grid-cols-3 gap-1.5 flex-1">
            {POSITIONS.map((p) => (
              <button
                key={p.key}
                onClick={() => save(true, current, p.key, currentSize)}
                disabled={busy}
                data-testid={`caption-position-${p.key}`}
                className={`rounded-md border px-2 py-1.5 text-[11px] font-semibold transition-colors ${
                  currentPos === p.key
                    ? "border-amber-500/60 bg-amber-500/10 text-amber-300"
                    : "border-white/10 bg-[#0A0A0A] text-zinc-500 hover:border-white/25"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {enabled && (
        <div className="mt-2 flex items-center gap-2" data-testid="caption-size-picker">
          <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold shrink-0">Text size</span>
          <div className="grid grid-cols-3 gap-1.5 flex-1">
            {SIZES.map((z) => (
              <button
                key={z.key}
                onClick={() => save(true, current, currentPos, z.key)}
                disabled={busy}
                data-testid={`caption-size-${z.key}`}
                className={`rounded-md border px-2 py-1.5 text-[11px] font-semibold transition-colors ${
                  currentSize === z.key
                    ? "border-amber-500/60 bg-amber-500/10 text-amber-300"
                    : "border-white/10 bg-[#0A0A0A] text-zinc-500 hover:border-white/25"
                }`}
              >
                {z.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {enabled && <CaptionPreview styleKey={current} position={currentPos} sizeKey={currentSize} text={sampleText} />}
    </div>
  );
}
